import { Fragment, type ReactNode } from "react";
import { IconCheck } from "./icons";

export function Section({
  title,
  aside,
  children,
}: {
  title?: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="section">
      {title || aside ? (
        <header className="section__head">
          {title ? <h2>{title}</h2> : null}
          {aside ? <div className="section__aside">{aside}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function Panel({
  children,
  tone,
  className,
}: {
  children: ReactNode;
  tone?: "danger";
  className?: string;
}) {
  return (
    <div className={["panel", tone ? `panel--${tone}` : "", className ?? ""].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

/** One line of a list panel: optional mark, title with detail, optional meta and action. */
export function Row({
  mark,
  title,
  detail,
  meta,
  action,
  children,
}: {
  mark?: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={mark ? "row row--mark" : "row"}>
      {mark ? <span className="row__mark">{mark}</span> : null}
      <div className="row__body">
        <strong className="row__title">{title}</strong>
        {detail ? <p className="row__detail">{detail}</p> : null}
        {children}
      </div>
      {meta ? <span className="row__meta">{meta}</span> : null}
      {action ? <div className="row__action">{action}</div> : null}
    </div>
  );
}

/** Data line whose parts never break internally, only at the separators. */
export function MetaLine({ text }: { text: string }) {
  const parts = text.split(" · ").filter(Boolean);
  return (
    <span className="data meta-line">
      {parts.map((part, index) => (
        <Fragment key={part}>
          <span>{part}</span>
          {index < parts.length - 1 ? " · " : null}
        </Fragment>
      ))}
    </span>
  );
}

export function Status({ tone, children }: { tone: "ok" | "warn" | "off"; children: ReactNode }) {
  return <span className={`status status--${tone}`}>{children}</span>;
}

export function Facts({ items }: { items: Array<{ label: string; value: ReactNode } | null | false> }) {
  return (
    <dl className="facts">
      {items.filter(Boolean).map((item) => {
        const fact = item as { label: string; value: ReactNode };
        return (
          <div key={fact.label}>
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

export function Points({ points }: { points: string[] }) {
  return (
    <ul className="points">
      {points.map((point) => (
        <li key={point}>
          <IconCheck />
          {point}
        </li>
      ))}
    </ul>
  );
}

export function Steps({ current }: { current: 1 | 2 | 3 }) {
  const steps = ["Konto", "Mitgliedschaft", "App"];
  return (
    <ol className="steps" aria-label="Einrichtung">
      {steps.map((label, index) => {
        const n = index + 1;
        const state = n < current ? "done" : n === current ? "now" : "todo";
        return (
          <li key={label} className={`steps__item is-${state}`} aria-current={state === "now" ? "step" : undefined}>
            <span className="steps__dot" aria-hidden="true">
              {state === "done" ? <IconCheck /> : n}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

export function Loading({ label }: { label: string }) {
  return (
    <div className="loading" role="status" aria-live="polite">
      <span className="loading__bar" />
      <span className="loading__bar loading__bar--short" />
      <span className="visually-hidden">{label}</span>
    </div>
  );
}

export function Notice({
  tone,
  children,
}: {
  tone: "error" | "success" | "info";
  children: ReactNode;
}) {
  return (
    <p className={`account-message account-message--${tone}`} role={tone === "error" ? "alert" : "status"}>
      {children}
    </p>
  );
}
